<template>
  <div class="toggle-token-container">
    <label class="switch" :for="checkboxId">
      <input :id="checkboxId" :value="toggleState" type="checkbox" :checked="toggleState" class="hidden" @input="updateInput" />
      <div
        class="toggle-token"
        :class="hasError && 'error'"
        tabindex="0"
        role="checkbox"
        :aria-checked="toggleState"
        :aria-label="ariaLabel"
        @keydown.space.prevent="updateInput"
      >
        <component
          :is="resolvedIcon"
          :class="iconClasses"
          :width="iconWidth"
          :height="iconHeight"
          :active="false"
          has-simple-label
        />
        <slot name="formattedText" />
        {{ text }}
      </div>
    </label>
  </div>
</template>

<script lang="ts">
import helperStatus from '../../helpers/status';
import { markComponentRaw } from '@/helpers/markComponentRaw';

export default {
  name: 'ToggleToken',
  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },
  props: {
    icon: {
      type: [Function, Object],
      required: true,
    },
    iconType: {
      type: String,
      required: false,
      default: 'icon',
      validator(value: string) {
        return ['badge', 'icon'].indexOf(value) !== -1;
      },
    },
    iconWidth: {
      type: Number || undefined,
      default: undefined,
      required: false,
    },
    iconHeight: {
      type: Number || undefined,
      default: undefined,
      required: false,
    },
    modelValue: {
      type: Boolean,
      default: false,
    },
    checkboxId: {
      type: String,
      default: '',
    },
    text: {
      type: String,
      default: '',
    },
    ariaLabel: {
      type: String,
      default: '',
      required: false,
    },
    status: {
      type: Number as () => helperStatus,
      default: helperStatus.Neutral,
    },
  },
  data() {
    return {
      toggleState: this.modelValue,
    };
  },
  computed: {
    resolvedIcon() {
      return markComponentRaw(this.icon);
    },
    iconClasses(): String {
      return this.iconType === 'badge' ? 'flex flex-col items-center' : 'mb-8';
    },
    hasError(): Boolean {
      return (this.status as any) === helperStatus.Error;
    },
  },
  watch: {
    modelValue(newValue) {
      this.toggleState = newValue;
    },
  },
  methods: {
    updateInput() {
      this.$emit('update:modelValue', !this.toggleState);
    },
  },
};
</script>

<style lang="scss" scoped>
@mixin toggle-icon-color($color) {
  :deep(.pill) {
    background-color: $color;
    color: theme('colors.foreground');
  }
  :deep(svg) {
    rect[stroke],
    path[stroke],
    circle[stroke],
    line[stroke] {
      stroke: $color;
    }
    path[fill] {
      fill: $color;
    }
  }
}

input:checked + .toggle-token {
  background-color: theme('colors.primary.subdued');
  padding: 1rem;
  border: 2px solid theme('colors.primary.DEFAULT');
  color: theme('colors.primary.DEFAULT');
  border-radius: 1rem;
  margin: 0;

  @include toggle-icon-color(theme('colors.primary.DEFAULT'));
}

.toggle-token {
  height: 100%;
  padding: 1rem;
  border: 1px solid theme('colors.grey.clickable');
  margin: 1px;
  border-radius: 1rem;
  background-color: theme('colors.grey.background.subdued');
  color: theme('colors.grey.clickable');
  font-size: 14px;
  font-weight: 500;
  line-height: 21px;
  letter-spacing: 0.07px;

  @media (hover: hover) {
    &:hover {
      background-color: theme('colors.foreground');
      box-shadow: 0px 2px 6px 0px rgba(5, 7, 15, 0.24);
      border-color: theme('colors.brand.contrast');
      color: theme('colors.brand.contrast');
      cursor: pointer;

      @include toggle-icon-color(theme('colors.brand.contrast'));
    }
  }

  &:focus-visible {
    background-color: theme('colors.foreground');
    box-shadow: 0px 2px 6px 0px rgba(5, 7, 15, 0.24);
    color: theme('colors.brand.contrast');
    margin: 0;
    outline: none;
    border: 2px dotted theme('colors.brand.contrast');

    @include toggle-icon-color(theme('colors.brand.contrast'));
  }

  &.error {
    border-color: theme('colors.critical.DEFAULT');
  }
}
</style>
